import { validateTvEnv } from '@streambase/config/tv';
import branding from '../../config/branding';

const tvEnv = validateTvEnv();
export const DEVELOPMENT_MODE = tvEnv.NODE_ENV !== 'production';

// Local-stack API ports (host-mapped by the test/dev docker stack: 4000->1337).
// Kept as named constants (no magic strings) so the e2e stack and this resolver
// agree on a single source.
export const LOCAL_API_ORIGIN = 'http://localhost:4000';
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

/**
 * Resolve the API origin the TV SPA should call.
 *
 * The TV web bundle is the SAME artifact across environments (one image
 * everywhere), so the endpoint must be chosen at RUNTIME from the browser's own
 * location — not baked at build time. When the SPA is served from localhost
 * (the e2e docker stack on :4002, or local dev) it must call the host-mapped
 * local API on :4000; the production domain (`https://api.<brand>`) is
 * unreachable from a localhost browser and a cross-origin call to it fails
 * (CORS / network) — which manifested as the TV e2e "Sem Conexão" pairing
 * failure. Anywhere else (real dev/prod domains) it calls `https://api.<brand>`.
 *
 * Falls back to the production origin when `window`/`location` is absent (SSR,
 * native runtime, or a non-DOM test) so non-web callers keep working.
 */
export const resolveApiOrigin = (): string => {
  const host =
    typeof window !== 'undefined' && window.location ? window.location.hostname : undefined;
  if (host && LOCAL_HOSTS.includes(host)) {
    return LOCAL_API_ORIGIN;
  }
  return `https://api.${branding.domain}`;
};

// REACT_APP_API_URL (webpack-inlined) OVERRIDES the runtime resolution when set
// (main hotfix: local/dev stacks with an explicit endpoint); a trailing /api is
// stripped so it is never doubled. Empty (the default) → runtime resolveApiOrigin.
const API_BASE = tvEnv.REACT_APP_API_URL.replace(/\/api$/, '') || resolveApiOrigin();

export const API_ENDPOINT = `${API_BASE}/api`;
export const AKAMAI_ENDPOINT = `https://${branding.cdnDomain}/`;
export const SOCKET_ENDPOINT = API_BASE;

export const GetHeaders = async (token?: string | boolean) => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return { headers };
};

export const ServerFetch = async (
  url: string,
  options: RequestInit,
  authenticated?: string | boolean,
) => {
  const { headers } = await GetHeaders(authenticated);
  try {
    const response = await fetch(url, { ...options, headers });
    // Interceptar 401 Unauthorized - sessão expirada
    if (response.status === 401) {
      console.warn('ServerFetch: 401 Unauthorized', url);
      return { error: true, status: 401, message: 'Unauthorized' };
    }
    try {
      return await response.json();
    } catch (err) {
      console.log('ServerParseError', err, url, { ...options, headers });
      return response;
    }
  } catch (error) {
    console.log('ServerFetchError', error);
    return false;
  }
};

export const GET = async (path: string, authenticated: string | boolean = false) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'GET',
    },
    authenticated,
  );
};

export const POST = async (
  path: string,
  body: unknown,
  authenticated: string | boolean = false,
) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    authenticated,
  );
};

export const PUT = async (path: string, body: unknown, authenticated: string | boolean = false) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
    authenticated,
  );
};

export const DELETE = async (path: string, authenticated: string | boolean = false) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'DELETE',
    },
    authenticated,
  );
};

export const ReadAddressesByZipCode = async (zipCode: string) => {
  try {
    const result = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
    return result.json();
  } catch (err) {
    return false;
  }
};

interface FileResult {
  path?: string;
  fileCopyUri?: string;
  uri?: string;
  croppedImage?: string;
  [key: string]: unknown;
}

export const ResolveUri = (result: FileResult | string | null): string | null => {
  if (typeof result === 'string') {
    return result.indexOf('file://') === -1 ? `file://${result}` : result;
  }
  const path = result?.path
    ? result.path
    : result?.fileCopyUri
      ? result.fileCopyUri
      : result?.uri
        ? result.uri
        : result?.croppedImage
          ? result.croppedImage
          : null;
  if (path == null) return null;
  return path.indexOf('file://') === -1 ? `file://${path}` : path;
};

export const PrepareFile = (result: FileResult | string | null) => {
  const uri = ResolveUri(result);
  const name = uri?.split('/').pop() ?? '';
  const match = /\.(\w+)$/.exec(name);
  const type = match ? `image/${match[1]}` : `image`;
  return { uri, name, type };
};

export const ServerUploadImage = async (result: FileResult | string | null) => {
  return await PostImage(PrepareFile(result));
};

export const PostImage = async (fileToUpload: {
  uri: string | null | undefined;
  name: string;
  type: string;
}) => {
  const formData = new FormData();
  formData.append('files', fileToUpload as unknown as Blob, fileToUpload.name);
  const { headers } = await GetHeaders(true);
  delete headers['Content-Type'];
  const response = await fetch(`${API_ENDPOINT}/upload`, {
    method: 'POST',
    body: formData,
    headers,
  });
  const responseJson = await response.json();
  return responseJson?.[0];
};
